/**
 * MyPendingQuotesPage
 *
 * Focused list of the CURRENT USER'S own pending quotations, sorted
 * oldest-first so the most overdue land at the top. Every row has the
 * shared OrderStatusButtons segmented control so a user can mark
 * Closed / Pending / Lost inline — no need to open the detail page.
 *
 * Reuses:
 *  • GET /api/quotations  (with status=pending + sort_order=asc; for
 *    admins we additionally pass salesperson_id=self.id so admins see
 *    only their own pending, matching the dashboard card count).
 *  • components/cart/OrderStatusButtons  (unchanged).
 *  • api.updateQuotationStatus  (existing endpoint).
 *
 * When a quote is marked Closed or Lost it is removed from the local
 * list immediately (it's no longer pending) — the dashboard count
 * refreshes when the user navigates back via the app-wide `navigate(-1)`
 * pattern.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatINR, formatISTDate, useAuth } from '../lib/store';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Clock, Eye, FileText, Loader2 } from 'lucide-react';
import { OrderStatusButtons } from '../components/cart/OrderStatusButtons';
import { logger } from '../lib/logger';

// Match QuotationsPage.daysPending: epoch-math floor, IST-neutral because
// only the day count matters for a badge on a mobile card.
const daysPending = (isoStr) => {
  if (!isoStr) return 0;
  const then = new Date(isoStr);
  if (Number.isNaN(then.getTime())) return 0;
  return Math.floor((Date.now() - then.getTime()) / 86400000);
};

export default function MyPendingQuotesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const isPrivileged = ['admin', 'manager', 'dispatcher'].includes(user?.role);

  // For staff/creator the backend already scopes /quotations to the
  // caller's own + on-behalf-of. For admin/manager/dispatcher we pass
  // salesperson_id=self.id so admins get their OWN pending, matching
  // the dashboard card's count (both use stale-pending-summary?mine=1).
  const fetchRows = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const params = {
        page: 1,
        limit: 200,
        status: 'pending',
        sort_order: 'asc',
      };
      if (isPrivileged) params.salesperson_id = user.id;
      const res = await api.getQuotations(params);
      setRows(res.data?.quotations || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  }, [user?.id, isPrivileged]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Local-optimistic removal: when a row is marked closed/lost by the
  // segmented control, it no longer belongs on this pending-only list.
  // We also decrement `total` so the header stays honest.
  const handleStatusChange = (id) => (newStatus) => {
    if (newStatus === 'pending') return; // stays on this list
    setRows(prev => prev.filter(r => r.id !== id));
    setTotal(t => Math.max(0, t - 1));
  };

  return (
    <div className="max-w-[1600px] mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6" data-testid="my-pending-quotes-page">
      <div className="flex items-center gap-2 mb-4 sm:mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="h-9 w-9 p-0"
          data-testid="my-pending-back-btn"
          aria-label="Back"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg sm:text-xl font-bold text-[#1A1C16]">My Pending Quotes</h1>
          <p className="text-xs sm:text-sm text-[#5C6150]" data-testid="my-pending-count-line">
            {loading ? 'Loading…' : `${total} pending — oldest first`}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-[#8F9485]">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading your pending quotes…
        </div>
      ) : rows.length === 0 ? (
        <Card className="border-[#E2E6D9]">
          <CardContent className="py-10 text-center">
            <FileText className="w-10 h-10 text-[#8F9485] mx-auto mb-3" />
            <p className="text-sm text-[#1A1C16] font-semibold">You have no pending quotes.</p>
            <p className="text-xs text-[#5C6150] mt-1">Great — nothing to chase right now.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2 sm:space-y-3">
          {rows.map((q) => {
            const dp = daysPending(q.created_at);
            const isOld = dp >= 30;
            const ageBadgeClass = isOld
              ? 'bg-red-50 text-red-700 border-red-200 font-semibold'
              : 'bg-[#F5F6F0] text-[#5C6150] border-[#E2E6D9]';
            return (
              <Card key={q.id} className="border-[#E2E6D9]" data-testid={`my-pending-row-${q.id}`}>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs sm:text-sm font-semibold text-[#556B2F]" data-testid={`my-pending-number-${q.id}`}>
                          {q.quotation_number}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] sm:text-xs ${ageBadgeClass}`}
                          data-testid={`my-pending-days-${q.id}`}
                        >
                          <Clock className="w-3 h-3" />
                          {dp}d pending
                        </span>
                      </div>
                      <p className="text-sm sm:text-base font-semibold text-[#1A1C16] truncate mt-1" data-testid={`my-pending-customer-${q.id}`}>
                        {q.customer?.name || q.customer_name || 'Unknown customer'}
                      </p>
                      <p className="text-[11px] sm:text-xs text-[#8F9485] mt-0.5">
                        Created {formatISTDate(q.created_at)}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <p className="font-bold font-mono-data text-sm sm:text-base text-[#1A1C16]" data-testid={`my-pending-value-${q.id}`}>
                        {formatINR(q.grand_total)}
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-[#556B2F] hover:bg-[#556B2F]/10"
                        onClick={() => navigate(`/quotations/${q.id}`)}
                        data-testid={`my-pending-view-${q.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" /> View
                      </Button>
                    </div>
                  </div>
                  <div className="mt-2">
                    <OrderStatusButtons
                      recordId={q.id}
                      status={q.order_status}
                      lostReason={q.lost_reason}
                      onStatusChange={(s, r) => api.updateQuotationStatus(q.id, { status: s, lost_reason: r })}
                      onChange={handleStatusChange(q.id)}
                    />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
